import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  useTheme,
  IconButton,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  WbSunny,
  NightsStay,
  WbTwilight,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '@/store/authStore';

interface DashboardHeaderProps {
  companyName?: string;
  companyLogo?: string | null;
  showRefresh?: boolean;
  onRefresh?: () => void;
  loading?: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  companyName = 'Nucleus',
  companyLogo = null,
  showRefresh = false,
  onRefresh,
  loading = false
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) return 'Good Morning';
    if (hour >= 12 && hour < 17) return 'Good Afternoon';
    if (hour >= 17 && hour < 21) return 'Good Evening';
    return 'Good Night';
  };

  const getGreetingIcon = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) return <WbSunny sx={{ color: '#FFA726', fontSize: 28 }} />;
    if (hour >= 12 && hour < 17) return <WbSunny sx={{ color: '#FF9800', fontSize: 28 }} />;
    if (hour >= 17 && hour < 21) return <WbTwilight sx={{ color: '#FF7043', fontSize: 28 }} />;
    return <NightsStay sx={{ color: '#5C6BC0', fontSize: 28 }} />;
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  const getFirstName = () => {
    if (!user?.name) return 'User';
    return user.name.split(' ')[0];
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        p: 3,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)'
          : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: '#fff'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Skeleton variant="circular" width={56} height={56} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Box>
            <Skeleton variant="text" width={200} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Skeleton variant="text" width={150} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: { xs: 'flex-start', sm: 'center' },
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 2,
      mb: 4,
      p: { xs: 2, sm: 3 },
      borderRadius: 3,
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)'
        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      color: '#fff',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Left Section - Company & Greeting */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Company Logo or Avatar */}
        {companyLogo ? (
          <Box
            component="img"
            src={companyLogo}
            alt={companyName}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              objectFit: 'contain',
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              p: 0.5
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              fontSize: '1.5rem',
              fontWeight: 700,
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            {companyName.charAt(0)}
          </Avatar>
        )}

        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              opacity: 0.9,
              fontSize: { xs: '1rem', sm: '1.1rem' }
            }}
          >
            {companyName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {getGreetingIcon()}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {getGreeting()}, {getFirstName()}!
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Section - Date, Time & Actions */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, sm: 3 },
        flexWrap: 'wrap'
      }}>
        {/* Date & Time */}
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              opacity: 0.95,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {formatDate()}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            {formatTime()}
          </Typography>
        </Box>

        {/* Refresh Button */}
        {showRefresh && onRefresh && (
          <Tooltip title="Refresh Dashboard">
            <IconButton
              onClick={onRefresh}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)'
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}

        {/* User Avatar */}
        <Tooltip title={user?.name || 'User'}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              fontWeight: 600,
              fontSize: '1rem',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            {getUserInitials()}
          </Avatar>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DashboardHeader;
